import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/shell/AppShell'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeAIButton } from '@/shell/ForgeAIButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Field } from '@/components/forge/Field'
import { Icon } from '@/components/forge/Icon'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { Panel } from '@/components/forge/Panel'
import { TablePagination } from '@/components/forge/TablePagination'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Mono, Num } from '@/components/forge/primitives'
import { DEFAULT_PAGE_SIZE } from '@/features/shiftHandovers/api'
import {
  useCreateShiftHandover,
  useShiftHandoverAccess,
  useShiftHandoverListStream,
  useShiftHandovers,
} from '@/features/shiftHandovers/queries'
import { trioOf } from '@/features/hierarchy/place'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { formatDateTime, formatDay, formatInteger } from '@/lib/format'
import { ShiftHandoverFormDialog } from './ShiftHandoverFormDialog'
import { useHandoverFormOptions } from './useHandoverFormOptions'

/**
 * Shift Handover. LIVE — `GET /v1/shifts/handovers`, kept live by its SSE
 * stream, with create, edit and delete.
 *
 * ── Everything that narrows the list is in the URL ──────────────────────
 * `?q=…&shift=…&from=…&to=…&sort=shift_date&page=2`. One handover is its own
 * route, `/app/shift-handovers/:handoverId` — a handover is the thing somebody
 * sends the next shift a link to. The `?handover=<id>` it used to open as a
 * dialog redirects there, because somebody has that link in a message.
 *
 * ── Live, not polled ─────────────────────────────────────────────────────
 * `useShiftHandoverListStream` refetches the page whenever any handover in the
 * account is filed or changed. A delete made elsewhere is the one change it
 * cannot see — the server does not announce it — so the list is also refetched
 * on focus as TanStack does by default.
 *
 * ── Not the demo archive ────────────────────────────────────────────────
 * `/app/handovers` is the prototype's archive, demo-only, still linked from the
 * Home card. This screen replaced the Shifts item in the rail.
 */
export function ShiftHandoverListPage() {
  const { t } = useTranslation(['app', 'common'])
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const access = useShiftHandoverAccess()

  const query = searchParams.get('q') ?? ''
  const shiftId = searchParams.get('shift') ?? null
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  const sortBy = searchParams.get('sort') === 'shift_date' ? 'shift_date' : 'created_at'
  const page = Number(searchParams.get('page')) || 1
  const legacyId = searchParams.get('handover')

  /* Filing a new one. Editing happens on the detail page. */
  const [creating, setCreating] = useState(false)
  /* A site, line or asset — through `PlaceFilter`, the dialog every place
     picker opens. Held in state rather than the URL, as on the anomaly list:
     the picker's value carries a name and a trail an id alone cannot rebuild. */
  const [place, setPlace] = useState(null)

  useDocumentTitle(`${t('screens.shiftHandovers')} · ${t('common:brand.name')}`)

  const setParam = (key, value) =>
    setSearchParams((previous) => {
      const url = new URLSearchParams(previous)
      if (value) url.set(key, value)
      else url.delete(key)
      /* Any change to what is being looked at returns to page 1. */
      if (key !== 'page') url.delete('page')
      return url
    })

  /* The search box is debounced into `?q=` so typing is not a request a key. */
  const [text, setText] = useState(query)
  useEffect(() => {
    if (text === query) return undefined
    const timer = setTimeout(() => setParam('q', text.trim() || null), 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setParam` is rebuilt every render
  }, [text, query])

  const params = useMemo(
    () => ({
      search: query || undefined,
      shiftId: shiftId ?? undefined,
      ...(trioOf(place) ?? {}),
      from: from || undefined,
      to: to || undefined,
      sortBy,
      sortDir: 'desc',
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
    [query, shiftId, place, from, to, sortBy, page],
  )

  const list = useShiftHandovers(params, { enabled: access.granted })
  useShiftHandoverListStream({ enabled: access.granted })
  const create = useCreateShiftHandover()

  const rows = list.data?.items ?? []

  const { shifts, shiftNames, shiftsFailed, canLink } = useHandoverFormOptions({ enabled: access.granted })

  const closeForm = () => {
    setCreating(false)
    create.reset()
  }

  const narrowed = Boolean(query || shiftId || place || from || to)
  const clear = () => {
    setText('')
    setPlace(null)
    setSearchParams((previous) => {
      const url = new URLSearchParams(previous)
      for (const key of ['q', 'shift', 'from', 'to', 'page']) url.delete(key)
      return url
    })
  }

  if (legacyId) return <Navigate to={`/app/shift-handovers/${encodeURIComponent(legacyId)}`} replace />

  return (
    <Page>
      <header className="mb-4 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="m-0 text-display text-balance">{t('shiftHandovers.title')}</h1>
          </div>
          <p className="mt-2 mb-0 text-body text-ink-dim">{t('shiftHandovers.subtitle')}</p>
        </div>

        <ForgeAIButton action="createShiftHandover" disabled={!access.granted} />
        <ForgeButton variant="primary" onClick={() => setCreating(true)} disabled={!access.granted || creating}>
          <Icon name="plus" size="xs" />
          {t('shiftHandovers.new')}
        </ForgeButton>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Field
          size="sm"
          label={t('shiftHandovers.search')}
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t('shiftHandovers.searchPlaceholder')}
        />

        <PlaceFilter
          id="handover-place"
          value={place}
          labelAbove
          onChange={(next) => {
            setPlace(next)
            setParam('page', null)
          }}
        />

        <ForgeSelect
          label={t('shiftHandovers.shiftFilter')}
          size="sm"
          value={shiftId ?? 'all'}
          onValueChange={(value) => setParam('shift', value === 'all' ? null : value)}
          options={[
            { value: 'all', label: t('shiftHandovers.anyShift') },
            ...[...shiftNames].map(([id, name]) => ({ value: id, label: name })),
          ]}
        />

        {/* `shift_date_from`/`shift_date_to`, inclusive, either alone. */}
        <Field
          size="sm"
          label={t('shiftHandovers.from')}
          type="date"
          value={from}
          onChange={(event) => setParam('from', event.target.value || null)}
        />
        <Field
          size="sm"
          label={t('shiftHandovers.to')}
          type="date"
          value={to}
          onChange={(event) => setParam('to', event.target.value || null)}
        />

        <ForgeSelect
          label={t('shiftHandovers.sortLabel')}
          size="sm"
          value={sortBy}
          onValueChange={(value) => setParam('sort', value === 'created_at' ? null : value)}
          options={[
            { value: 'created_at', label: t('shiftHandovers.sort.created_at') },
            { value: 'shift_date', label: t('shiftHandovers.sort.shift_date') },
          ]}
        />

        {narrowed ? (
          <ForgeButton variant="ghost" size="sm" onClick={clear}>
            {t('shiftHandovers.clear')}
          </ForgeButton>
        ) : null}

        <span className="ms-auto text-body-sm text-ink-dim">
          <Num>{t('shiftHandovers.count', { total: formatInteger(list.data?.total ?? 0) })}</Num>
        </span>
      </div>

      {access.isPending ? (
        <SkeletonRows count={6} />
      ) : !access.granted ? (
        <EmptyState icon="lock" title={t('shiftHandovers.forbidden')} body={t('shiftHandovers.forbiddenBody')} />
      ) : list.isError ? (
        <ErrorState error={list.error} title={t('shiftHandovers.loadFailed')} onRetry={() => list.refetch()} />
      ) : list.isPending ? (
        <SkeletonRows count={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="cal"
          title={narrowed ? t('shiftHandovers.emptySearchTitle') : t('shiftHandovers.emptyTitle')}
          body={narrowed ? t('shiftHandovers.emptySearchBody') : t('shiftHandovers.emptyBody')}
          action={
            narrowed ? (
              <ForgeButton onClick={clear}>{t('shiftHandovers.clear')}</ForgeButton>
            ) : (
              <ForgeButton variant="primary" onClick={() => setCreating(true)}>
                {t('shiftHandovers.new')}
              </ForgeButton>
            )
          }
        />
      ) : (
        <Panel flush>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-body-sm">
              <thead>
                <tr className="bg-sunken">
                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className="eyebrow border-b border-hairline px-4 py-2.5 text-start whitespace-nowrap"
                    >
                      {t(`shiftHandovers.columns.${column}`)}
                    </th>
                  ))}
                  <th scope="col" className="border-b border-hairline px-4 py-2.5">
                    <span className="sr-only">{t('common:actions.viewDetails')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-subtle last:border-b-0 even:bg-row-alt">
                    <td className="px-4 py-2.5">
                      <Link to={`/app/shift-handovers/${row.id}`} className="hover:text-on-forge">
                        <Mono className="font-semibold">{row.serialNumber}</Mono>
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {shiftNames.get(row.shiftId) ?? t('shiftHandovers.unknownShift')}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-ink-dim">
                      <Num>{formatDay(row.shiftDate)}</Num>
                    </td>
                    <td className="px-4 py-2.5 text-ink-dim">
                      {[row.hierarchy.plant, row.hierarchy.line, row.hierarchy.machine].filter(Boolean).join(' › ') ||
                        '—'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-ink-dim">
                      <Num>
                        {t('shiftHandovers.carried', {
                          workOrders: formatInteger(row.workOrderCount),
                          logbook: formatInteger(row.logbookEntryCount),
                          anomalies: formatInteger(row.anomalyCount),
                        })}
                      </Num>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-ink-dim">
                      <Num>{formatDateTime(row.createdAt)}</Num>
                      {access.isMine(row) ? <span className="ms-2">{t('shiftHandovers.byYou')}</span> : null}
                    </td>
                    <td className="px-4 py-2.5 text-end">
                      <ForgeButton size="sm" asChild>
                        <Link to={`/app/shift-handovers/${row.id}`}>
                          <span className="sr-only">{t('shiftHandovers.open', { id: row.serialNumber })}</span>
                          <Icon name="right" size="xs" className="mirror-rtl" />
                        </Link>
                      </ForgeButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            total={list.data?.total ?? 0}
            page={list.data?.page ?? page}
            pageSize={list.data?.pageSize ?? DEFAULT_PAGE_SIZE}
            label={t('shiftHandovers.title')}
            onPageChange={(next) => setParam('page', next > 1 ? String(next) : null)}
          />
        </Panel>
      )}

      {creating ? (
        <ShiftHandoverFormDialog
          /* Mounted only while open, so it seeds afresh every time. */
          open
          handover={{}}
          shifts={shifts}
          shiftsFailed={shiftsFailed}
          canLink={canLink}
          pending={create.isPending}
          error={create.error}
          onClose={closeForm}
          onSubmit={(payload) =>
            create.mutate(payload, {
              onSuccess: (saved) => {
                closeForm()
                toast(t('shiftHandovers.toast.created', { id: saved.serialNumber }))
                navigate(`/app/shift-handovers/${saved.id}`)
              },
              /* Field refusals are drawn on the form, which stays open. */
              onError: () => toast(t('shiftHandovers.toast.saveFailed'), 'error'),
            })
          }
        />
      ) : null}
    </Page>
  )
}

const COLUMNS = ['serial', 'shift', 'date', 'place', 'carried', 'created']
